import { EmptyState, Spinner } from "@hexling/ui";
import { Link } from "@tanstack/react-router";
import { PageTitle } from "../../components/Layout";
import { Markdown } from "../../components/Markdown";
import { useContent, useSettings } from "../../data/AppData";

const LEVEL: Record<string, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export function LessonsPage() {
  const content = useContent();
  const settings = useSettings();
  if (content.isPending || !settings) return <Spinner />;
  if (!settings.features.lessons)
    return (
      <EmptyState title="Lessons are hidden" action={<Link to="/settings">Show lessons</Link>}>
        You turned lessons off in Settings.
      </EmptyState>
    );
  const courses = content.data?.list("course") ?? [];
  if (!courses.length)
    return (
      <EmptyState glyph="📖" title="Lessons haven't downloaded yet">
        Connect once to save them on this device.
      </EmptyState>
    );

  return (
    <>
      <PageTitle
        title="Lessons"
        subtitle="Start with Witchcraft 101, then follow whatever calls to you."
      />
      <ul className="label-list">
        {courses.map((c) => (
          <li key={c.slug}>
            <Link className="label-row" to="/lessons/$course" params={{ course: c.slug }}>
              <span className="label-row__glyph" aria-hidden="true">
                {String(c.data.icon ?? "")}
              </span>
              <span>
                <span className="label-row__name">{c.name}</span>
                <span className="label-row__blurb">{c.summary}</span>
              </span>
              <span className="label-row__meta">{LEVEL[String(c.data.level)] ?? ""}</span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

export function CoursePage({ course }: { course: string }) {
  const content = useContent();
  if (content.isPending) return <Spinner />;
  const index = content.data;
  const c = index?.get("course", course);
  if (!index || !c)
    return <EmptyState title="Course not found" action={<Link to="/lessons">All lessons</Link>} />;
  const lessons = index.list("lesson").filter((l) => l.data.course_slug === course);
  return (
    <>
      <PageTitle title={c.name} subtitle={c.summary} back={{ to: "/lessons", label: "Lessons" }} />
      <ol className="lesson-list">
        {lessons.map((l) => (
          <li key={l.slug}>
            <Link
              to="/lessons/$course/$lesson"
              params={{ course, lesson: l.slug }}
              className="lesson-list__link"
            >
              {l.name}
            </Link>
            <span className="hx-muted">
              {" "}
              {String(l.data.estimated_minutes)} min
              {l.data.status === "draft_outline" ? ", coming soon" : ""}
            </span>
          </li>
        ))}
      </ol>
    </>
  );
}

export function LessonPage({ course, lesson }: { course: string; lesson: string }) {
  const content = useContent();
  if (content.isPending) return <Spinner />;
  const index = content.data;
  const l = index?.get("lesson", lesson);
  const c = index?.get("course", course);
  if (!index || !l || !c)
    return <EmptyState title="Lesson not found" action={<Link to="/lessons">All lessons</Link>} />;
  const siblings = index.list("lesson").filter((x) => x.data.course_slug === course);
  const i = siblings.findIndex((x) => x.slug === lesson);
  const next = siblings[i + 1];
  const body = typeof l.data.body_markdown === "string" ? l.data.body_markdown : "";
  const objectives = Array.isArray(l.data.objectives) ? (l.data.objectives as string[]) : [];

  return (
    <article>
      <PageTitle
        title={l.name}
        subtitle={`${c.name}, lesson ${i + 1} of ${siblings.length}`}
        back={{ to: "/lessons/$course", label: c.name, params: { course } }}
      />
      <p className="plate__lede">{l.summary}</p>
      {objectives.length > 0 && (
        <section className="hx-panel" aria-labelledby="objectives">
          <h2 id="objectives">By the end you'll be able to</h2>
          <ul>
            {objectives.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        </section>
      )}
      {body ? (
        <Markdown source={body} />
      ) : (
        <EmptyState glyph="✒️" title="This lesson is being written">
          We're writing and fact-checking it now. The outline above shows what it will cover.
        </EmptyState>
      )}
      {next && (
        <p>
          Next:{" "}
          <Link to="/lessons/$course/$lesson" params={{ course, lesson: next.slug }}>
            {next.name}
          </Link>
        </p>
      )}
    </article>
  );
}
